import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MaskedLines, Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { api, wordpressApi } from "../lib/api";
import { ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SEO, { JsonLd, optimizeMetaDescription, optimizeMetaTitle } from "../components/SEO";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}
function stripHtml(html) {

  const div = document.createElement("div");

  div.innerHTML = html || "";

  return div.textContent || div.innerText || "";

}

function getPostTags(post) {
  return (post._embedded?.["wp:term"] || [])
    .flat()
    .filter((term) => term.taxonomy === "post_tag")
    .map((term) => term.name);
}



export function BlogIndex() {
 
  const [items, setItems] = useState([]);

useEffect(() => {

  wordpressApi.get("", {

params: {

  endpoint: "posts",

  context: "edit",

  _embed: true,

  categories_exclude: 49,

},

  })

    .then(r => {

     const posts = r.data.map(post => ({

  id: post.id,

  title: post.title?.rendered || "",

  slug: post.slug || "",

  excerpt: stripHtml(post.excerpt?.rendered),

        content: post.content?.rendered || "",

        cover_image:
  post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",

        author: "Dorado9 Team",

        tags: getPostTags(post),

        published_at: post.date || "",

      }));

      setItems(posts);

    })

    .catch(() => {});

}, []);

  return (
    <>
       <section className="relative pt-40 lg:pt-71.8 pb-40 lg:pb-17 px-6 lg:px-12 bg-brand-bg overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1920&q=60&auto=format&fit=crop"
            alt=""
            aria-hidden="true"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-[0.18] mix-blend-luminosity"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,18,22,0.72) 0%, rgba(20,18,22,0.55) 45%, var(--bg) 100%)" }} />
        </div>
        <div className="absolute inset-0 grain opacity-25" />
        <div className="absolute -top-40 -right-40 w-[60vw] h-[60vw] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(102,76,221,0.34), transparent 60%)" }} />
        <div className="absolute -bottom-40 -left-40 w-[45vw] h-[45vw] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(17,245,186,0.10), transparent 65%)" }} />

        <div className="max-w-[1600px] mx-auto relative">
  <div className="max-w-6xl">
    <h1 className="eyebrow mb-6 flex items-center gap-3">
      <span className="w-8 h-px bg-brand-purple" />
      Blogs
    </h1>

    <p
      className="font-display text-[13vw] sm:text-[10vw] lg:text-[8.5vw] leading-[1.1] tracking-tighter uppercase text-white"
      data-testid="services-hero-title"
    >
      <MaskedLines
        lines={[
          <>INSIGHTS.</>,
          <>Built from<span className="font-serif-accent text-brand-purple lowercase" > real experience.</span></>,
        ]}
        stagger={0.14}
        delay={0.25}
      />
    </p>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.85 }}
      className="mt-6 max-w-3xl text-zinc-400 text-lg leading-relaxed"
    >
      Practical insights from real campaigns, real client work, and real results. Explore strategies, breakdowns, and ideas that help brands grow with clarity and confidence.
    </motion.p>
  </div>
</div>
      </section>
      <section className="bg-brand-bg py-16 lg:py-24 px-6 lg:px-12 border-t border-zinc-900">
        <div className="max-w-[1600px] mx-auto">
          {items.length > 0 && (
            <Reveal>
              <Link to={`/blog/${items[0].slug}`} data-testid={`blog-featured-${items[0].slug}`} className="group block mb-24 grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
                <div className="lg:col-span-7 spotlight-card aspect-[16/10] overflow-hidden border border-zinc-900">
                  <img src={items[0].cover_image} alt={items[0].title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]" />
                </div>
                <div className="lg:col-span-5">
                  <p className="eyebrow mb-4">Featured · {formatDate(items[0].published_at)}</p>
                  <h2 className="font-display text-3xl lg:text-5xl tracking-tight uppercase leading-[0.95]">{items[0].title}</h2>
                  <p className="mt-6 text-zinc-400 leading-relaxed">{items[0].excerpt}</p>
                  <span className="mt-8 inline-flex items-center gap-2 link-underline text-brand-mint text-sm uppercase tracking-[0.24em]">Read essay <ArrowUpRight size={14}/></span>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {items.slice(1).map((b, i) => (
              <Reveal key={b.id} delay={i * 0.06}>
                <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden border border-zinc-900 spotlight-card">
                    <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]" />
                  </div>
                  <p className="mt-5 eyebrow">{formatDate(b.published_at)}</p>
                  <h3 className="font-display text-2xl mt-3 tracking-tight uppercase leading-tight group-hover:text-brand-mint transition-colors duration-500">{b.title}</h3>
                  <p className="mt-3 text-zinc-500 text-sm">{b.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);

  

useEffect(() => {

  // Load the current post by WordPress slug
  wordpressApi.get("", {
    params: {
      endpoint: "posts",
      slug: slug,
      _embed: true
    }
  })
    .then(r => {

      if (!r.data || r.data.length === 0) {
        setB(null);
        return;
      }

      const post = r.data[0];

      const coverImage =
        post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "";

      const blog = {
        id: String(post.id),
        title: post.title?.rendered || "",
        slug: post.slug || "",
        excerpt: stripHtml(post.excerpt?.rendered),
        content: post.content?.rendered || "",
        cover_image: coverImage,
        author: "Dorado9 Team",
        tags: getPostTags(post),
        published_at: post.date || "",
        wordpress_id: post.id,
        seo_title: post.meta?.seo_title || post.meta?.meta_title || "",
        seo_description: post.meta?.seo_description || post.meta?.meta_description || "",
      };

      setB(blog);
    })
    .catch(() => {
      setB(null);
    });


  // Load recent posts
  wordpressApi.get("", {
    params: {
      endpoint: "posts",
      _embed: true,
      categories_exclude: 49,
    }
  })
    .then(r => {

      const posts = r.data.map(post => {

        const coverImage =
          post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "";

        return {
          id: String(post.id),
          title: post.title?.rendered || "",
          slug: post.slug || "",
         excerpt: stripHtml(post.excerpt?.rendered),
          content: post.content?.rendered || "",
          cover_image: coverImage,
          author: "Dorado9 Team",
          tags: getPostTags(post),
          published_at: post.date || "",
          wordpress_id: post.id
        };

      });

      setRecentPosts(posts);

    })
    .catch(() => {});

}, [slug]);

  if (!b) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: b.title,
    description: b.excerpt,
    image: b.cover_image || undefined,
    datePublished: b.published_at || undefined,
    dateModified: b.published_at || undefined,
    mainEntityOfPage: `https://dorado9.com/blog/${b.slug}`,
    author: { "@type": "Organization", name: b.author },
    publisher: { "@id": "https://dorado9.com/#organization" },
  };

  return (
    <>
      <SEO
        schema={false}
        title={optimizeMetaTitle(b.seo_title || b.title)}
        description={optimizeMetaDescription(b.seo_description || b.excerpt || b.content)}
      />
      <JsonLd id="blog-post" data={articleSchema} />
      <section className="relative pt-40 lg:pt-56 pb-12 px-6 lg:px-12 bg-brand-bg">
        <div className="max-w-[1200px] mx-auto">
          <Link to="/blog" className="eyebrow inline-flex items-center gap-2 mb-8 hover:text-zinc-100 transition-colors">← Blogs</Link>
          <p className="eyebrow mb-4">{formatDate(b.published_at)} · {b.author}</p>
          <h1 className="font-display text-4xl lg:text-6xl tracking-tighter uppercase leading-[0.95]">
            <MaskedLines lines={[b.title]} />
          </h1>
        </div>
      </section>

      <section className="bg-brand-bg px-6 lg:px-12 py-12">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <div className="aspect-[16/9] overflow-hidden border border-zinc-900">
              <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-bg py-16 px-6 lg:px-12">
       <div className="max-w-[1200px] mx-auto grid lg:grid-cols-[minmax(0,780px)_340px] gap-20">

  {/* LEFT COLUMN */}
  <div>
    <Reveal>
      <div
        className="blog-content max-w-none text-zinc-300 leading-[1.85]"
        dangerouslySetInnerHTML={{ __html: b.content }}
      />
    </Reveal>

    <div className="mt-14 flex flex-wrap gap-2 ">
      {b.tags.map((t) => (
        <span
          key={t}
          className="text-xs uppercase tracking-widest border border-zinc-800 rounded-full px-3 py-1.5"
        >
          {t}
        </span>
      ))}
    </div>
  </div>

  {/* RIGHT COLUMN */}
  <aside className="hidden lg:block sticky top-32 h-fit">

   <div className=" border border-zinc-800 bg-[#1b1b1d] p-7 overflow-hidden">
  <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-brand-purple/15 blur-3xl bg-brand-mint/20 blur-5xl" />
      <h3 className="font-display text-2xl uppercase mb-6">
        More Insights
      </h3>

      <div className="space-y-6">

        <div className="space-y-6">
  {recentPosts
    .filter(post => post.slug !== b.slug)
    .slice(0, 4)
    .map((post, index) => (
      <Link
        key={post.id}
        to={`/blog/${post.slug}`}
        className={`block group ${
          index !== 3 ? "border-b border-zinc-800 pb-5" : ""
        }`}
      >
        <p className="text-white group-hover:text-brand-mint transition-colors">
          {post.title}
        </p>

        <span className="text-zinc-500 text-sm">
          {formatDate(post.published_at)}
        </span>
      </Link>
    ))}
</div>

      </div>
    </div>

  </aside>

</div>
      </section>
    </>
  );
}
